import axiosInstance from "axios"
import { TApi } from "../utils/@types/api"
import { TProduct } from "../utils/@types/data/product"
import { jwtDecode } from "jwt-decode"
import TParams from "../utils/@types/api/params"
import { formatDate } from "date-fns"
import getStore from "../store"
import { TEventData } from "../utils/@types/data/event"

const backUrl = process.env.REACT_APP_BACKEND_URL
const mailingUrl = process.env.REACT_APP_EMAIL_BACKEND_URL

const mockDomain = process.env.REACT_APP_MOCK_DOMAIN ?? ""

// Telefone (E.164, só dígitos, ex.: 5511999999999) do WhatsApp da empresa que
// recebe o pedido "Quero receber meus ingressos aqui no whatsapp".
export const STORE_WHATSAPP_E164 = (
  process.env.REACT_APP_STORE_WHATSAPP_E164 ?? ""
).replace(/\D/g, "")

const axios = axiosInstance.create()
axios.defaults.baseURL = backUrl

export const checkTokenExpiration = (token: string) => {
  try {
    const decoded = jwtDecode(token)

    const iat = (decoded.iat as number) * 1000
    const now = +new Date().getTime().toFixed(0)

    const limit = 50 * 60 * 1000
    const exp = iat + limit

    return now > exp
  } catch (error) {
    return true
  }
}

axios.interceptors.request.use(function (config) {
  try {
    const localToken = localStorage.getItem("token")

    const { controllers } = getStore.getState()

    const domain = window.location.hostname

    config.headers["X-Store-Domain"] = domain

    if (process.env.NODE_ENV === "development") {
      config.headers["X-Store-Domain"] = mockDomain
    }

    // Token explícito na requisição (página pública com o token do pedido no
    // link): não troca o header nem mexe na sessão do aparelho.
    if (config.headers.Authorization) return config

    if (localToken) {
      if (localToken === "undefined") {
        sessionStorage.removeItem("user")
        localStorage.removeItem("token")
        controllers.user.clear()

        window.location.reload()
      } else {
        const isTokenExpired = checkTokenExpiration(localToken)

        if (isTokenExpired) {
          sessionStorage.removeItem("user")
          localStorage.removeItem("token")
          controllers.user.clear()

          window.location.reload()
        } else config.headers.Authorization = `Bearer ${localToken}`
      }
    }

    return config
  } catch (error) {
    return config
  }
})

/*
 * Service check
 */

// getSubdomainStatus e getEvents batem no mesmo endpoint (sem eventId) em sequência
// no cold-start (routes -> EventSelect). Cacheia a 1a resposta pra 2a não refazer a
// mesma request de ~MB.
let cachedSubdomainInfo: any = null
let subdomainInfoPromise: Promise<any> | null = null

function isLicenseExpired(timestamp: number) {
  const date = new Date(timestamp);

  const correctedTimestamp =
    timestamp + date.getTimezoneOffset() * 60 * 1000;

  return Date.now() >= correctedTimestamp;
}

const fetchSubdomainInfo = () => {
  if (cachedSubdomainInfo) return Promise.resolve(cachedSubdomainInfo)

  if (!subdomainInfoPromise) {
    subdomainInfoPromise = axios
      .get(`${backUrl}/ecommerce/list/getInfo`, {
        headers: {
          Authorization: undefined,
        },
      })
      .then((res) => {
        cachedSubdomainInfo = res.data
        return res.data
      })
      .finally(() => {
        subdomainInfoPromise = null
      })
  }

  return subdomainInfoPromise
}

const getSubdomainStatus: TApi["get"]["subdomainStatus"] = async () => {
  return new Promise(async (resolve) => {
    try {
      await fetchSubdomainInfo()
        .then((info) => {
          if (
              info && info.success && 
              info.info?.hasECommerce !== false &&              // Is ListaPix available for this client 
              isLicenseExpired(info.info?.expireAt) === false   // Is License not expired
            ) {
            
            const store = getStore.getState()

            if (!store.event)
              store.controllers.event.setData(info as TEventData)

            resolve({ ok: true })
          } else {
            resolve({ ok: false })
          }
        })
        .catch(() => {
          resolve({ ok: false })
        })
    } catch {
      resolve({ ok: false })
    }
  })
}

/*
 * Getters
 */

const getQrCode: TApi["get"]["qrcode"] = async ({ order }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const parsed: TParams["get"]["qrcode"]["order"] = {
        ...order,
        // buyerName: order.payer.first_name,
        transaction_amount: order.transaction_amount / 100,
      }

      await axios
        .post(`${backUrl}/ecommerce/payment/orders/qrcode`, parsed)
        .then((res) => {
          const info = res.data

          if (info) {
            resolve({
              ok: true,
              data: info.data,
            })
          } else {
            reject({
              error: "Erro ao processar o qrcode. Tente novamente mais tarde",
            })
          }
        })
        .catch((err) => {
          const data = err?.response?.data ?? {}
          resolve({
            ok: false,
            error:
              data.error ??
              "Erro ao carregar o qrcode. Tente novamente mais tarde",
            code: data.code ?? data.error,
            details: data.details,
          })
        })
    } catch (error) {
      reject({
        error: "Erro ao carregar o qrcode. Tente novamente mais tarde",
      })
    }
  })
}

const getEvents: TApi["get"]["events"] = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      await fetchSubdomainInfo()
        .then(async (data) => {
          const list = data.events

          if (list) {
            const availableEvents = list
              .filter((event: any) => Boolean(event.is_ecommerce))
              .map((ev: any) => ({
                ...ev,
                dk: data.dk,
              }))

            resolve({
              ok: true,
              data: {
                organizer: data.info,
                events: availableEvents,
              },
            })
          } else {
            reject({
              error: "Erro ao carregar os eventos. Tente novamente mais tarde",
            })
          }
        })
        .catch((err) => {
          resolve({
            ok: false,
            error: "Erro ao carregar os eventos. Tente novamente mais tarde",
          })
        })
      // }
    } catch (error) {
      reject({
        error: "Erro ao carregar os eventos. Tente novamente mais tarde",
      })
    }
  })
}

const getEventInfo: TApi["get"]["eventInfo"] = async ({ eventId }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const req = await axios.get(`/ecommerce/event/getInfo?eventId=${eventId}`, {
        headers: {
          "X-event-id": eventId,
        },
      })

      const event = req.data.event ?? null

      if (event) {
        const eventData: TEventData = {
          ...req.data.info,
          ...event,
          corporateFantasyName: req.data.info.name,
          dk: req.data.dk,
        }

        resolve({
          ok: true,
          data: eventData,
        })
      } else throw new Error()
    } catch (error) {
      resolve({
        ok: false,
        error:
          "Erro ao carregar as informações do evento. Tente novamente mais tarde",
      })
    }
  })
}

const getProducts: TApi["get"]["products"] = async ({ eventId }) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .get(`/ecommerce/product/getList?eventId=${eventId}`, {
          data: JSON.stringify({ userKey: "fjLZQ8D29PfCwI2SbKwPgB7r7gD3" }),
        })
        .then((res) => {
          const receivedList = res.data

          const list = Array.isArray(receivedList) ? receivedList : []

          let parsed: TProduct[] = []
          list.forEach((i) => {
            const activeBatchData = i.batches.find((b: any) =>
              Boolean(b.active),
            )

            const isTicketActive =
              i.active || i.batches.some((b: any) => Boolean(b.active))

            if (activeBatchData) {
              const obj = {
                id: String(i.product_id),
                name: i.name,
                image: i.image,
                created_at: i.created_at,
                updated_at: i.updated_at,
                active: isTicketActive,
                batch_id: activeBatchData.batch_id,
                batch_name: activeBatchData.batch_name,
                group_id: i.group_id ?? "",
                group_name: i.group_name
                  ? i.group_name.replace("-loja", "").replace(`-${eventId}`, "")
                  : "",
                description: i.description ?? null,
                quantity: activeBatchData.quantity,
                price_sell: activeBatchData.price_sell,
                // Mesa fields (null for regular products)
                mesa_type: i.mesa_type ?? null,
                mesa_sector: i.mesa_sector ?? null,
                mesa_number: i.mesa_number ?? null,
                mesa_seats: i.mesa_seats ?? null,
                mesa_ticket_mode: i.mesa_ticket_mode ?? null,
                mesa_map_image: i.mesa_map_image ?? null,
                mesa_layout_id: i.mesa_layout_id ?? null,
              }

              parsed.push(obj)
            }
          })

          resolve({
            ok: true,
            data: { list: parsed },
          })
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Erro ao listar produtos. Tente novamente mais tarde",
          })
        })
    } catch (error) {
      reject({
        error: "Erro ao listar produtos. Tente novamente mais tarde",
      })
    }
  })
}

const getMyTickets: TApi["get"]["myTickets"] = async ({
  eventId,
  eventName,
}) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .get(`/ecommerce/getMyTickets?eventId=${eventId}`)
        .then(async ({ data }) => {
          let returnList: any[] = []

          const list = Array.isArray(data.shoppings) ? data.shoppings : []

          let pms: Promise<any>[] = []

          const ordersIds = new Set(
            list
              .filter((shop: any) => {
                const isForEvent = shop.qr_data.split("/")[0] === eventId
                return isForEvent
              })
              .map((shop: any) => shop.order_id),
          )

          ordersIds.forEach((id) => {
            pms.push(
              axios
                .get(`/${eventId}/ecommerce/orders/${id}`)
                .then(async (res) => {
                  const purchase = res.data

                  const someTicket =
                    purchase.products.length > 0 ? purchase.products[0] : null

                  if (someTicket) {
                    const ticketDetailsReq = await axios.get(
                      `/${eventId}/validate_ticket/${someTicket.qr_label}`,
                    )

                    if (
                      ticketDetailsReq.status === 200 &&
                      ticketDetailsReq.data
                    ) {
                      const orderPaymentStatus =
                        ticketDetailsReq.data.detail.status

                      const fullPurchase = {
                        ...purchase,
                        status: orderPaymentStatus,
                      }

                      returnList.push(fullPurchase)
                    }
                  }
                })
                .catch(() => {
                  // throw new Error()
                }),
            )
          })

          await Promise.all(pms)

          const fuse = new Date().getTimezoneOffset() / 60

          const parsedList = returnList.map((item) => {
            const d = new Date(item.products[0].date)
            d.setHours(d.getHours() - fuse)
            const dString = d.toString()

            const pList = item.products.map((p: any) => {
              const pd = new Date(p.date)
              pd.setHours(pd.getHours() - fuse)
              const pdString = pd.toString()

              const nDate = formatDate(pdString, "yyyy-MM-dd HH:mm:ss")

              return { ...p, date: nDate }
            })

            return {
              ...item,
              date: dString,
              quantity: item.products.length,
              products: pList,
              total_price: item.products.reduce(
                (amount: number, ticket: any) => amount + ticket.price_total,
                0,
              ),
            }
          })

          resolve({
            ok: true,
            data: { list: parsedList },
          })
        })
        .catch((err) => {
          // 403: a sessão é o token de um pedido (compra sem login), não a conta.
          // 401: não há token nenhum. Nos dois casos falta entrar com o telefone.
          const status = err?.response?.status
          if (status === 403 || status === 401) {
            resolve({
              ok: false,
              code: "LOGIN_REQUIRED",
              error: "Entre com seu telefone para continuar.",
            })
            return
          }
          resolve({
            ok: false,
            error: "Erro ao listar produtos. Tente novamente mais tarde",
          })
        })
    } catch (error) {
      reject({
        error: "Erro ao listar produtos. Tente novamente mais tarde",
      })
    }
  })
}

/*
 * Login
 */

const CHECKOUT_CUSTOMER_ERROR =
  "Não foi possível continuar com a compra. Tente novamente."

// Checkout sem login: garante o comprador no backend. Não devolve token; a
// sessão do pós-pagamento é o token do pedido que o sign devolve.
const ensureCheckoutCustomer: TApi["post"]["checkout"]["customer"] = async ({
  phone,
  name,
  email,
}) => {
  return new Promise(async (resolve) => {
    try {
      await axios
        .post("/ecommerce/checkout/customer", {
          fone: phone.replace(/\D/g, ""),
          name,
          email,
        })
        .then((res) => {
          if (res.data?.success) resolve({ ok: true, data: { success: true } })
          else resolve({ ok: false, error: CHECKOUT_CUSTOMER_ERROR })
        })
        .catch((err) => {
          const message = err?.response?.data?.message
          resolve({
            ok: false,
            error:
              typeof message === "string" && message
                ? message
                : CHECKOUT_CUSTOMER_ERROR,
          })
        })
    } catch {
      resolve({ ok: false, error: CHECKOUT_CUSTOMER_ERROR })
    }
  })
}

const REQUEST_CODE_ERROR =
  "Não foi possível enviar o código. Tente novamente em alguns minutos."

// O backend gera, guarda e envia o código (WhatsApp; se falhar, SMS) e diz
// por qual canal foi. A loja nunca vê o código.
const requestCode: TApi["post"]["login"]["requestCode"] = async ({
  phone,
  smsOnly,
  dk,
}) => {
  return new Promise(async (resolve) => {
    try {
      await axios
        .post("/ecommerce/requestCode", {
          fone: phone,
          database: dk,
          delivery: "server",
          ...(smsOnly ? { channel: "sms" } : {}),
        })
        .then((res) => {
          const channel = res.data?.channel
          if (res.data?.success && (channel === "whatsapp" || channel === "sms")) {
            resolve({
              ok: true,
              data: { success: true, channel, message: "Código enviado com sucesso." },
            })
          } else resolve({ ok: false, error: REQUEST_CODE_ERROR })
        })
        .catch((err) => {
          const status = err?.response?.status
          const message = err?.response?.data?.message

          // Sem cadastro = 400 { success: false }; outro 400 (ex.: "Erro ao
          // enviar token.", domínio sem configuração) cai na mensagem padrão.
          if (status === 400 && err?.response?.data?.success === false) {
            resolve({ ok: false, error: "Número não cadastrado, confira seu telefone." })
          } else if ((status === 429 || status === 502) && typeof message === "string" && message) {
            resolve({ ok: false, error: message })
          } else resolve({ ok: false, error: REQUEST_CODE_ERROR })
        })
    } catch {
      resolve({ ok: false, error: REQUEST_CODE_ERROR })
    }
  })
}

const validateCode: TApi["post"]["login"]["validateCode"] = async ({
  phone,
  code,
  email,
  name,
  dk,
}) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .post("/ecommerce/authenticate", {
          fone: phone,
          code: code,
          database: dk,
        })
        .then((res) => {
          const uObj = {
            ...res.data.user,
            email: email ?? res.data.user.email,
            name: name ?? res.data.user.name,
            cpf: res.data.roleData.cpf,
            fone: res.data.roleData.fone,
          }

          localStorage.setItem("token", res.data.token)

          resolve({ ok: true, data: uObj })
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Algo deu errado. Tente novamente mais tarde.",
          })
        })
    } catch (error) {
      reject({ error: "Erro ao requisitar código. Tente novamente mais tarde" })
    }
  })
}

/*
 * Mailing
 */

const sendEmail: TApi["post"]["mail"]["sendEmail"] = async (mailInfo) => {
  return new Promise(async (resolve, reject) => {
    try {
      const formData = new FormData()

      formData.append("file", mailInfo.file)
      formData.append("logoWebstoreUrl", mailInfo.logoWebstoreUrl)
      formData.append("logo", mailInfo.logo)

      formData.append("eventName", mailInfo.eventName)
      formData.append("eventDate", mailInfo.eventDate)
      formData.append("eventTime", mailInfo.eventTime)
      formData.append("eventLocal", mailInfo.eventLocal)

      formData.append("buyerName", mailInfo.buyerName)
      formData.append("organizerName", mailInfo.organizerName)
      formData.append("organizerDocument", mailInfo.organizerDocument)

      formData.append("purchaseCode", mailInfo.purchaseCode)
      formData.append("purchaseTime", mailInfo.purchaseTime)
      formData.append("purchaseValue", mailInfo.purchaseValue)
      formData.append("purchaseTaxes", mailInfo.purchaseTaxes)
      formData.append("purchaseItems", mailInfo.purchaseItems)
      formData.append("purchaseStatus", mailInfo.purchaseStatus)

      formData.append("targetEmail", mailInfo.targetEmail)

      await axios
        .post("/api/sendemail", formData, {
          baseURL: mailingUrl,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => {
          const status = res.data.sended

          if (Boolean(status)) resolve({ ok: true, data: { sended: status } })
          else throw new Error()
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Algo deu errado. Tente novamente mais tarde.",
          })
        })
    } catch (error) {
      reject({ error: "Erro ao enviar email. Tente novamente mais tarde" })
    }
  })
}

const communicationLog: TApi["post"]["communicationLog"] = async (payload) => {
  return new Promise(async (resolve) => {
    try {
      await axios
        .post(`${backUrl}/ecommerce/communication-logs`, payload)
        .then((res) => {
          resolve({ ok: true, data: res.data })
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Erro ao registrar log de comunicação.",
          })
        })
    } catch {
      resolve({
        ok: false,
        error: "Erro ao registrar log de comunicação.",
      })
    }
  })
}

// Status da instância Z-API do serviço de mailing. Qualquer falha (rede,
// timeout, resposta inesperada) conta como instância inativa: o fluxo "guardar
// no meu WhatsApp" não depende dela.
const getInstanceStatus: TApi["get"]["instanceStatus"] = async () => {
  return new Promise(async (resolve) => {
    try {
      await axios
        .get("/check-instance", { baseURL: mailingUrl, timeout: 8000 })
        .then((res) => {
          const body = res.data ?? {}
          const active = Boolean(body.connected ?? body.active ?? false)

          resolve({ ok: true, data: { active } })
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Não foi possível verificar o status do WhatsApp.",
          })
        })
    } catch {
      resolve({
        ok: false,
        error: "Não foi possível verificar o status do WhatsApp.",
      })
    }
  })
}

// Resultado do envio automático do ingresso (backend). Qualquer falha (rota
// antiga, 401/404, rede, status desconhecido) vira ok:false: o pós-pagamento
// trata como "não enviado" e mostra o fluxo de guardar.
const VOUCHER_AUTO_STATUSES = ["disabled", "pending", "failed", "sent"]

const getVoucherAutoStatus: TApi["get"]["voucherAutoStatus"] = async ({
  orderId,
  eventId,
}) => {
  return new Promise(async (resolve) => {
    const fail = () =>
      resolve({ ok: false, error: "Não foi possível consultar o envio." })

    try {
      await axios
        .get(
          `/ecommerce/orders/${encodeURIComponent(orderId)}/whatsapp/status`,
          { params: { event_id: eventId }, timeout: 8000 },
        )
        .then((res) => {
          const status = res.data?.status
          if (!VOUCHER_AUTO_STATUSES.includes(status)) return fail()

          if (status === "sent") {
            resolve({
              ok: true,
              data: { status: "sent", phone: String(res.data.phone ?? "") },
            })
          } else {
            resolve({ ok: true, data: { status } })
          }
        })
        .catch(fail)
    } catch {
      fail()
    }
  })
}

const sendWhatsapp: TApi["post"]["whatsapp"]["sendWhatsapp"] = async ({
  targetPhone,
  base64File,
  fileName,
  caption,
  unsafe,
}) => {
  return new Promise(async (resolve, reject) => {
    try {
      const formattedPhone = `55${targetPhone}`.replace(/\D/g, "")

      await axios
        .post(
          "/api/sendwhatsapp",
          {
            phone: formattedPhone,
            document: `data:application/pdf;base64,${base64File}`,
            fileName: fileName,
            caption: caption,
            ...(unsafe !== undefined ? { unsafe } : {}),
          },
          {
            baseURL: mailingUrl,
          },
        )
        .then((res) => {
          const status = res.data.sended

          if (Boolean(status)) resolve({ ok: true, data: res.data })
          else throw new Error()
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Algo deu errado. Tente novamente mais tarde.",
          })
        })
    } catch (error) {
      reject({ error: "Erro ao enviar o código. Tente novamente mais tarde" })
    }
  })
}

/*
 * Voucher por WhatsApp (backend gera o PDF e envia pelo SMS Barato)
 */

const VOUCHER_FALLBACK_ERROR =
  "Não foi possível enviar pelo WhatsApp agora. Tente novamente em alguns minutos."

// Envio síncrono no backend: 2–4s normalmente, até ~20s no pior caso.
const sendVoucherWhatsapp: TApi["post"]["voucher"]["sendWhatsapp"] = async ({
  orderId,
  eventId,
  phone,
}) => {
  return new Promise(async (resolve) => {
    try {
      const digits = phone ? phone.replace(/\D/g, "") : ""

      await axios
        .post(
          `/ecommerce/orders/${encodeURIComponent(orderId)}/whatsapp`,
          { event_id: eventId, ...(digits ? { phone: digits } : {}) },
          { timeout: 30000 },
        )
        .then((res) => {
          if (res.data?.sent === true) {
            resolve({
              ok: true,
              data: { sent: true, phone: String(res.data.phone ?? digits) },
            })
          } else {
            resolve({ ok: false, error: VOUCHER_FALLBACK_ERROR })
          }
        })
        .catch((err) => {
          const status = err?.response?.status
          const message = err?.response?.data?.error

          if (status === 401) {
            resolve({
              ok: false,
              code: "UNAUTHORIZED",
              error:
                "Sua sessão expirou. Entre novamente para reenviar seus ingressos.",
            })
            return
          }

          resolve({
            ok: false,
            code: status ? String(status) : "NETWORK",
            error:
              typeof message === "string" && message.trim() !== ""
                ? message
                : VOUCHER_FALLBACK_ERROR,
          })
        })
    } catch {
      resolve({ ok: false, error: VOUCHER_FALLBACK_ERROR })
    }
  })
}

/*
 * Purchase
 */

// Header da página pública: o token do pedido vem do link, não da sessão.
const withToken = (token?: string) =>
  token ? { headers: { Authorization: `Bearer ${token}` } } : undefined

const orderPaymentStatus: TApi["get"]["orderPaymentStatus"] = async ({
  eventId,
  orderId,
}) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .get(`/${eventId}/ecommerce/orders/${orderId}/status`)
        .then((res) => {
          const purchase = res.data

          resolve({ ok: true, data: purchase })
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Algo deu errado. Tente novamente mais tarde.",
          })
        })
    } catch (error) {
      reject({
        error:
          "Erro ao monitorar status do pagamento. Tente novamente mais tarde",
      })
    }
  })
}

/** Status do pedido ("validado" | "cancelamento" | ...) a partir de um de seus ingressos. */
const getTicketStatus: TApi["get"]["ticketStatus"] = async ({
  eventId,
  qrLabel,
  token,
}) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .get(`/${eventId}/validate_ticket/${qrLabel}`, withToken(token))
        .then((res) => {
          const status = res.data?.detail?.status

          if (typeof status !== "string") {
            resolve({
              ok: false,
              error: "Não foi possível verificar o status do pedido.",
            })
            return
          }

          resolve({ ok: true, data: { status } })
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Algo deu errado. Tente novamente mais tarde.",
          })
        })
    } catch (error) {
      reject({
        error: "Erro ao verificar status do pedido. Tente novamente mais tarde",
      })
    }
  })
}

const signPurchase: TApi["post"]["purchase"]["sign"] = async (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .post("/ecommerce/buyTicket", data)
        .then((res) => {
          const purchase = res.data

          resolve({ ok: true, data: purchase })
        })
        .catch((err) => {
          const body = err?.response?.data ?? {}
          resolve({
            ok: false,
            error: body.error ?? "Algo deu errado. Tente novamente mais tarde.",
            code: body.code ?? body.error,
            details: body.details,
          })
        })
    } catch (error) {
      reject({ error: "Erro ao solicitar tickets. Tente novamente mais tarde" })
    }
  })
}

/** Valida o cupom para o carrinho (rota pública da loja). */
const validateCoupon: TApi["post"]["coupon"]["validate"] = async (data) => {
  return new Promise(async (resolve) => {
    try {
      await axios
        .post("/ecommerce/coupons/validate", data)
        .then((res) => {
          const d = res.data ?? {}

          resolve({
            ok: true,
            data: {
              code: d.coupon?.code ?? data.code.trim().toUpperCase(),
              type: d.coupon?.type,
              value: d.coupon?.value,
              subtotal: Number(d.subtotal) || 0,
              discountTotal: Number(d.discount_total) || 0,
              taxTotal: Number(d.tax_total) || 0,
              chargeClient: d.charge_client !== false,
              total: Number(d.total) || 0,
              cartKey: "",
            },
          })
        })
        .catch((err) => {
          const body = err?.response?.data ?? {}
          resolve({
            ok: false,
            error:
              body.error ??
              "Não foi possível validar o cupom. Tente novamente.",
            code: body.code,
          })
        })
    } catch (error) {
      resolve({
        ok: false,
        error: "Não foi possível validar o cupom. Tente novamente.",
      })
    }
  })
}

const mpGenerate: TApi["post"]["purchase"]["mpGenerate"] = async ({
  body,
  iKey,
}) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .post("https://api.mercadopago.com/v1/payments", body, {
          headers: {
            "X-Idempotency-Key": iKey,
          },
          baseURL: "",
        })
        .then((res) => {
          const purchase = res.data

          resolve({ ok: true, data: purchase })
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Algo deu errado. Tente novamente mais tarde.",
          })
        })
    } catch (error) {
      reject({ error: "Erro ao solicitar tickets. Tente novamente mais tarde" })
    }
  })
}

const confirmPurchase: TApi["post"]["purchase"]["confirm"] = async (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .post("/ecommerce/confirmBuyTicket", data)
        .then((res) => {
          const purchase = res.data

          resolve({ ok: true, data: purchase })
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Algo deu errado. Tente novamente mais tarde.",
          })
        })
    } catch (error) {
      reject({ error: "Erro ao solicitar tickets. Tente novamente mais tarde" })
    }
  })
}

const getPaymentPublicKey: TApi["get"]["paymentPublicKey"] = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .get(`${backUrl}/ecommerce/payment/public-key`)
        .then((res) => {
          if (res.data?.ok && res.data.data?.publicKey) {
            resolve({ ok: true, data: res.data.data })
          } else {
            resolve({ ok: false, error: "Falha ao obter chave do Mercado Pago." })
          }
        })
        .catch(() => {
          resolve({ ok: false, error: "Falha ao obter chave do Mercado Pago." })
        })
    } catch {
      reject({ ok: false, error: "Falha ao obter chave do Mercado Pago." })
    }
  })
}

const payCard: TApi["post"]["purchase"]["payCard"] = async (body) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .post(`${backUrl}/ecommerce/payment/orders/card`, body)
        .then((res) => {
          if (res.data?.ok) resolve({ ok: true, data: res.data.data })
          else
            resolve({
              ok: false,
              error: res.data?.error ?? "Pagamento recusado.",
              details: res.data?.details,
            })
        })
        .catch((err) => {
          const data = err?.response?.data ?? {}
          resolve({
            ok: false,
            error: data.error ?? "Erro ao processar pagamento por cartão.",
            details: data.details,
          })
        })
    } catch {
      reject({ ok: false, error: "Erro ao processar pagamento por cartão." })
    }
  })
}

const getPurchase: TApi["get"]["purchaseInfo"] = async ({
  eventId,
  orderId,
  token,
}) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios
        .get(`/${eventId}/ecommerce/orders/${orderId}`, withToken(token))
        .then((res) => {
          const purchase = res.data

          const productsList = purchase.products.map((t: any) => {
            const fuse = new Date().getTimezoneOffset() / 60

            const d = new Date(t.date)
            d.setHours(d.getHours() - fuse)
            const dString = d.toString()

            return {
              ...t,
              date: dString,
            }
          })

          const info = {
            ...purchase,
            products: productsList,
          }

          resolve({ ok: true, data: info })
        })
        .catch(() => {
          resolve({
            ok: false,
            error: "Algo deu errado. Tente novamente mais tarde.",
          })
        })
    } catch (error) {
      reject({ error: "Erro ao solicitar tickets. Tente novamente mais tarde" })
    }
  })
}

export const Api: TApi = {
  get: {
    subdomainStatus: getSubdomainStatus,
    qrcode: getQrCode,
    events: getEvents,
    eventInfo: getEventInfo,
    myTickets: getMyTickets,
    products: getProducts,
    purchaseInfo: getPurchase,
    orderPaymentStatus: orderPaymentStatus,
    ticketStatus: getTicketStatus,
    paymentPublicKey: getPaymentPublicKey,
    instanceStatus: getInstanceStatus,
    voucherAutoStatus: getVoucherAutoStatus,
  },
  post: {
    checkout: {
      customer: ensureCheckoutCustomer,
    },
    login: {
      requestCode: requestCode,
      validateCode: validateCode,
    },
    coupon: {
      validate: validateCoupon,
    },
    purchase: {
      sign: signPurchase,
      mpGenerate: mpGenerate,
      confirm: confirmPurchase,
      payCard: payCard,
    },
    mail: {
      sendEmail,
    },
    whatsapp: {
      sendWhatsapp,
    },
    voucher: {
      sendWhatsapp: sendVoucherWhatsapp,
    },
    communicationLog,
  },
}
